import type { OvertimeBreakdown } from '../../utils/overtimeUtils';
import type jsPDF from 'jspdf';

export type WorkType = 'etude' | 'suivi' | 'mise_en_service' | 'chantier';
export type TabType = 'analytics' | 'rentabilite' | 'paie' | 'historique';
export type FilterType = 'active' | 'archived' | 'all' | 'risque';
export type FilterMode = 'week' | 'month' | 'year';
export type HistoryViewMode = 'monthly' | 'yearly';

// KPIs financiers pour l'historique d'affaire
export interface ProjectFinancials {
  devisAmount: number | null;    // Montant devis HT (null = non renseigné)
  totalAchats: number;           // Somme des achats validés
  totalMoCost: number;           // Heures réelles × taux horaire employé
  marge: number | null;          // devisAmount - totalAchats - totalMoCost (si devis renseigné)
  margePercent: number | null;   // marge / devisAmount × 100
}

// Types pour l'historique d'affaire
export interface MonthlyHistoryPoint {
  month: string;           // Format: "2024-01" (YYYY-MM)
  monthLabel: string;      // Format: "Janvier 2024"
  etude: number;
  suivi: number;
  mise_en_service: number;
  chantier: number;
  total: number;
  cumulative: number;      // Total cumulé depuis le début du projet
}

export interface ProjectHistory {
  projectId: string;
  projectCode: string;
  projectName: string;
  createdAt: string;
  isActive: boolean;
  budgetChantier: number;
  budgetBE: number;
  monthlyData: MonthlyHistoryPoint[];
  totalHours: {
    etude: number;
    suivi: number;
    mise_en_service: number;
    chantier: number;
    total: number;
  };
  progressChantier: number;  // percentage
  progressBE: number;        // percentage
  firstEntryDate: string;
  lastEntryDate: string;
  spanMonths: number;
}

export interface ReportRow {
  employeeId: string;
  employeeName: string;
  role: string;
  customWeeklyHours: number | null;
  totalHours: number;
  totalMeals: number;
  totalTransport: number;
  totalTrajet: number;
  zones: Record<string, number>;
  overtime: number;
  // Détail heures supplémentaires selon semaines paires/impaires
  overtime25: number;       // HS majorées à 25%
  overtime50: number;       // HS majorées à 50%
  sundayHours: number;      // Heures dimanche (100%)
  weeklyDetails: (OvertimeBreakdown & { weekNumber: number; year: number })[];
  // Grands Déplacements
  gdDaysCount: number;           // Nombre de jours en GD
  gdTotalAmount: number;         // Montant total des indemnités GD (€)
  gdTotalTravelHours: number;    // Total des heures de route GD
}

export interface ProjectStats {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  budgetChantier: number;
  budgetBE: number;
  consumedChantier: number;
  consumedBE: number;
  // Financier
  devisAmount: number | null;
  totalAchats: number;
  totalMoCost: number;
  marge: number | null;
  margePercent: number | null;
  // Facturation client
  totalFacture: number;
  resteAFacturer: number | null;
}

export interface RawEntry {
  id: string;
  hours_normal: number;
  hours_saturday: number;
  hours_major: number;
  meals_count: number;
  travel_zone: number | string | null;
  indemnity_transport: number | null;
  indemnity_trajet: number | null;
  project_id: string;
  work_date: string;
  // Données Grands Déplacements
  is_grand_deplacement?: boolean;
  gd_amount?: number | null;
  gd_travel_hours?: number | null;
  weekly_sheets: {
    status: string;
    year?: number;
    week_number?: number;
    override_weekly_hours?: number | null;
    employee: { id: string; full_name: string; role: string; custom_weekly_hours?: number | null; } | null;
  } | null;
  be_ventilations?: { work_type: string; hours: number }[];
}

export interface VentilationRaw {
  time_entry_id: string;
  work_type: WorkType;
  hours: number;
}

export interface AnalyticsData {
  global: Record<WorkType, number>;
  byProject: Record<string, {
    name: string;
    code: string;
    etude: number;
    suivi: number;
    mise_en_service: number;
    chantier: number;
    total: number;
  }>;
  byEmployee: Record<string, {
    name: string;
    total: number;
  }>;
  totalHours: number;
  totalEmployees: number;
  avgHoursPerEmployee: number;
  gdDaysCount: number; // Nombre de jours en Grand Déplacement
}

export interface JsPDFWithAutoTable extends jsPDF {
  lastAutoTable: { finalY: number };
}

// Constantes
export { MONTHS_FR } from '../../utils/dateUtils';

// Utilitaires pour les semaines ISO
export const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

export const getWeekStartDate = (year: number, week: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
};

export const getWeekEndDate = (weekStart: Date): Date => {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
};
